import { Injectable } from '@angular/core';
import { BulkAccountDeleteObject } from '../../component/trial-balance/trial-balance.component';
import { FinancialStatementAccount } from '../../models/common/FinancialStatementAccount';
import { AccountFilterData } from '../../utils/common/utilityClasses';
import { FinancialStatementService } from '../common/financial-statement.service';
import { TrialBalanceTotals } from '../../utils/trial-balance/utils';


@Injectable()

export class TrialBalanceService {

  constructor(
    private fsService: FinancialStatementService,
  ) { }

  /**
   * Performs data setup for financial statements, updating trial balance totals and
   * separating manual accounts from the financial statement accounts.
   *
   * This function calculates totals directly on the provided `totals` object,
   * considering the selected date and period from the `accountFilterDataObject`.
   * Manual accounts are filtered from the original `tbData`, and the modified
   * `tbData` and filtered manual accounts are returned as an object.
   *
   * @returns An object containing the modified tbData and the filtered manualAccounts.
   */
  dataSetup(accountFilterDataObject: AccountFilterData,
    tbData: FinancialStatementAccount[], totals: TrialBalanceTotals, manualAccounts: FinancialStatementAccount[]) {
    if (accountFilterDataObject?.selectedDate && accountFilterDataObject?.selectedPeriod) {
      this.fsService.calculateAccountTotals(
        tbData, accountFilterDataObject.selectedDate, accountFilterDataObject.selectedPeriod);
    } else {
      this.fsService.calculateAccountTotals(tbData);
    }
    this.calculateTotals(totals, tbData);
    manualAccounts = tbData.filter((account) => account.isManual);
    tbData = tbData.filter((account) => !account.isManual);
    return { tbData, manualAccounts };
  }

  calculateTotals(totals: TrialBalanceTotals, accounts: FinancialStatementAccount[]) {
    totals.totalBeforeChanges = accounts.reduce((accTotal, account) => accTotal + account.sums.sumITP, 0);
    totals.totalAdjustments = accounts.reduce((accTotal, account) => accTotal + account.sums.manualDebit - account.sums.manualCredit, 0);
    totals.totalAfterChanges = totals.totalBeforeChanges + totals.totalAdjustments;
  }

  getThinkTwiceModalForManualAccountDescription(accountToBeDeleted: FinancialStatementAccount): string {
    const count = accountToBeDeleted?.glTransactions?.length;
    if (count === 0) {
      return 'There are no entries associated with this account, so deleting it will not affect any transactions.';
    }
    return `${count} transaction${count > 1 ? 's' : ''} associated with this account will be deleted.`;
  }

  initBulkDeleteMode(): BulkAccountDeleteObject {
    return {
      isActive: true,
      infoText: 'Select accounts to delete',
      selectedAccounts: [],
      bulkDeleteAllCheckbox: { selected: false, minus: false },
    };
  }

  setBulkDeleteAllCheckbox(bulkAccountDeleteObject: BulkAccountDeleteObject, manualAccountsLength: number) {
    const allSelected = bulkAccountDeleteObject.selectedAccounts.length === manualAccountsLength;
    return {
      selected: allSelected,
      minus: bulkAccountDeleteObject.selectedAccounts.length > 0 && !allSelected,
    };
  }

  filterAccountsByNameOrCode(filterValue: string, manualReserved: FinancialStatementAccount[], tbReserved: FinancialStatementAccount[]) {
    const filteredManualAccounts = [...this.filterAccountByNameOrCode(manualReserved, filterValue)];
    const filteredTbData = [...this.filterAccountByNameOrCode(tbReserved, filterValue)];
    return { filteredManualAccounts, filteredTbData };
  }

  private filterAccountByNameOrCode(reserved: FinancialStatementAccount[], inputValue: string): FinancialStatementAccount[] {
    return reserved.filter((account) =>
      account.name?.toLowerCase().includes(inputValue.toLowerCase()) ||
      account.code?.toLowerCase().includes(inputValue.toLowerCase()) ||
      (account.glTransactions && account.glTransactions.some((transaction) =>
        transaction.journalEntryCode?.toLowerCase().includes(inputValue.toLowerCase()),
      )),
    );
  }


  private resetTotals(totals: TrialBalanceTotals): void {
    totals.totalAfterChanges = 0;
    totals.totalBeforeChanges = 0;
    totals.totalAdjustments = 0;
  }
}

